import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, Crown, Medal, Target, Rocket, Star, Zap, ArrowUp, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RankWidget = ({ user }) => {
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchLeaderboardData();
    }
  }, [user]);

  const fetchLeaderboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const leaderboard = response.data.leaderboard || [];
      const sorted = [...leaderboard].sort((a, b) => b.current_month - a.current_month);
      const userRank = sorted.findIndex(a => a.email === user?.email) + 1;
      const userData = sorted.find(a => a.email === user?.email);
      const userAhead = userRank > 1 ? sorted[userRank - 2] : null;
      const gapToNext = userAhead ? userAhead.current_month - (userData?.current_month || 0) : 0;
      
      setRankData({
        rank: userRank,
        totalAgents: sorted.length,
        sales: userData?.current_month || 0,
        growth: userData?.growth || 0,
        userAhead: userAhead?.agent_name,
        gapToNext: gapToNext,
        badges: userData?.badges || [],
        monthName: response.data.current_month?.name
      });
    } catch (error) {
      console.error('Failed to fetch rank data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMotivationalContent = (rank, totalAgents, gapToNext, userAhead) => {
    if (rank === 1) {
      return {
        title: "You're the Champion!",
        subtitle: "Leading the pack!",
        message: "You're #1 this month! Keep defending your title and setting the standard for excellence.",
        gradient: "from-amber-400 via-yellow-500 to-amber-400",
        bgGradient: "from-amber-50 to-yellow-50",
        borderColor: "border-amber-300",
        icon: Crown,
        iconBg: "bg-gradient-to-br from-amber-400 to-yellow-500",
        animation: "animate-pulse"
      };
    } else if (rank === 2) {
      return {
        title: "Silver Position!",
        subtitle: "So close to gold!",
        message: `Just ${gapToNext} more sales to overtake ${userAhead}. You've got this!`,
        gradient: "from-slate-300 via-slate-400 to-slate-300",
        bgGradient: "from-slate-50 to-slate-100",
        borderColor: "border-slate-300",
        icon: Medal,
        iconBg: "bg-gradient-to-br from-slate-300 to-slate-500"
      };
    } else if (rank === 3) {
      return {
        title: "Bronze Fighter!",
        subtitle: "On the podium!",
        message: `${gapToNext} sales away from #2. Push harder and climb up!`,
        gradient: "from-orange-300 via-amber-400 to-orange-300",
        bgGradient: "from-orange-50 to-amber-50",
        borderColor: "border-orange-300",
        icon: Medal,
        iconBg: "bg-gradient-to-br from-orange-400 to-amber-500"
      };
    } else if (rank <= 5) {
      return {
        title: `Rank #${rank} - Almost There!`,
        subtitle: "The podium is in sight!",
        message: `Only ${gapToNext} sales to beat ${userAhead} and move up! Every sale counts!`,
        gradient: "from-purple-400 via-pink-500 to-purple-400",
        bgGradient: "from-purple-50 to-pink-50",
        borderColor: "border-purple-300",
        icon: Star,
        iconBg: "bg-gradient-to-br from-purple-500 to-pink-500"
      };
    } else if (rank <= 10) {
      return {
        title: `Rank #${rank} - Rising Star!`,
        subtitle: "Keep climbing!",
        message: `${gapToNext} sales to pass ${userAhead}. Stay focused and consistent!`,
        gradient: "from-blue-400 via-cyan-500 to-blue-400",
        bgGradient: "from-blue-50 to-cyan-50",
        borderColor: "border-blue-300",
        icon: Rocket,
        iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500"
      };
    } else {
      return {
        title: `Rank #${rank}`,
        subtitle: "Every champion started here!",
        message: `${gapToNext} sales to beat ${userAhead}. Today is your day to rise!`,
        gradient: "from-emerald-400 via-teal-500 to-emerald-400",
        bgGradient: "from-emerald-50 to-teal-50",
        borderColor: "border-emerald-300",
        icon: Target,
        iconBg: "bg-gradient-to-br from-emerald-500 to-teal-500"
      };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
        <div className="h-24 bg-slate-100 rounded-xl"></div>
      </div>
    );
  }

  if (!rankData || rankData.rank === 0) {
    return null;
  }

  const content = getMotivationalContent(
    rankData.rank, 
    rankData.totalAgents, 
    rankData.gapToNext,
    rankData.userAhead
  );
  
  const IconComponent = content.icon;

  return (
    <div className={`bg-gradient-to-br ${content.bgGradient} rounded-2xl border-2 ${content.borderColor} overflow-hidden shadow-lg hover:shadow-xl transition-shadow`}>
      {/* Top Banner */}
      <div className={`bg-gradient-to-r ${content.gradient} p-1`}></div>
      
      <div className="p-6">
        <div className="flex items-start justify-between">
          {/* Left - Rank Info */}
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 ${content.iconBg} rounded-2xl flex items-center justify-center shadow-lg ${content.animation || ''}`}>
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-800">{content.title}</h3>
              <p className="text-sm font-semibold text-slate-500">{content.subtitle}</p>
            </div>
          </div>
          
          {/* Right - Stats */}
          <div className="text-right">
            <div className="flex items-center justify-end gap-2 mb-1">
              <Trophy className="w-5 h-5 text-amber-500" />
              <span className="text-3xl font-black bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                #{rankData.rank}
              </span>
            </div>
            <span className="text-xs text-slate-500">of {rankData.totalAgents} agents</span>
          </div>
        </div>

        {/* Motivational Message */}
        <div className="mt-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-white/50">
          <p className="text-slate-700 font-medium leading-relaxed">
            {content.message}
          </p>
        </div>

        {/* Stats Row */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-slate-800">{rankData.sales}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Sales</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className={`text-2xl font-bold ${rankData.growth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {rankData.growth > 0 ? '+' : ''}{rankData.growth}%
            </p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Growth</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-purple-600">{rankData.badges.length}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Badges</p>
          </div>
        </div>

        {/* Gap to Next */}
        {rankData.rank > 1 && rankData.gapToNext > 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            <ArrowUp className="w-4 h-4 text-emerald-500" />
            <span className="text-slate-600">
              <strong className="text-slate-800">{rankData.gapToNext}</strong> more sales to reach <strong>#{rankData.rank - 1}</strong>
            </span>
          </div>
        )}

        {/* Month indicator */}
        <div className="mt-3 text-xs text-slate-400 text-center">
          {rankData.monthName} Rankings
        </div>
      </div>
    </div>
  );
};

export default RankWidget;
